import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";
import { OrderService } from "@/gen/omnios/v1/order_connect";
import { PartyService } from "@/gen/omnios/v1/party_connect";
import { formatNumber } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
	ArrowRight,
	Bot,
	Check,
	Mail,
	Package,
	Puzzle,
	Settings,
	ShoppingCart,
	Sparkles,
	Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useOnboarding } from "./OnboardingContext";

const catalogClient = createClient(CatalogService, transport);
const orderClient = createClient(OrderService, transport);
const partyClient = createClient(PartyService, transport);

interface StatProps {
	icon: React.ComponentType<{ className?: string }>;
	label: string;
	value: number | undefined;
	loading: boolean;
	color: string;
}

function Stat({ icon: Icon, label, value, loading, color }: StatProps) {
	return (
		<div className="flex items-center gap-3">
			<div
				className={`flex h-9 w-9 items-center justify-center rounded-xl ${color}`}
			>
				<Icon className="h-4 w-4" />
			</div>
			<div>
				{loading ? (
					<Skeleton className="h-5 w-10" />
				) : (
					<p className="text-lg font-semibold tabular-nums">
						{value != null ? formatNumber(value) : 0}
					</p>
				)}
				<p className="text-xs text-muted-foreground">{label}</p>
			</div>
		</div>
	);
}

const NEXT_STEPS = [
	{
		icon: Package,
		titleKey: "next_import_title",
		descKey: "next_import_desc",
		to: "/catalog/products",
	},
	{
		icon: Sparkles,
		titleKey: "next_enrich_title",
		descKey: "next_enrich_desc",
		to: "/settings/catalog",
	},
	{
		icon: Mail,
		titleKey: "next_email_title",
		descKey: "next_email_desc",
		to: "/settings/team-email",
	},
	{
		icon: Bot,
		titleKey: "next_assistant_title",
		descKey: "next_assistant_desc",
		to: "/assistant",
	},
	{
		icon: Puzzle,
		titleKey: "next_plugins_title",
		descKey: "next_plugins_desc",
		to: "/settings/plugins",
	},
	{
		icon: Settings,
		titleKey: "next_settings_title",
		descKey: "next_settings_desc",
		to: "/settings",
	},
];

export function GraduationScreen() {
	const { t } = useTranslation("onboarding");
	const { dismiss, mode } = useOnboarding();
	const navigate = useNavigate();

	const products = useQuery({
		queryKey: ["onboarding", "grad", "products"],
		queryFn: () =>
			catalogClient.listProducts({ page: { pageSize: 1, pageToken: "" } }),
	});
	const orders = useQuery({
		queryKey: ["onboarding", "grad", "orders"],
		queryFn: () =>
			orderClient.listOrders({ page: { pageSize: 1, pageToken: "" } }),
	});
	const parties = useQuery({
		queryKey: ["onboarding", "grad", "parties"],
		queryFn: () =>
			partyClient.listParties({ page: { pageSize: 1, pageToken: "" } }),
	});

	return (
		<div className="mx-auto max-w-lg space-y-6 py-8">
			{/* Header */}
			<div className="text-center space-y-2">
				<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
					<Check className="h-7 w-7 text-primary" />
				</div>
				<h1 className="text-2xl font-semibold tracking-tight">
					{t("graduation_title")}
				</h1>
				<p className="text-sm text-muted-foreground">
					{mode === "chat"
						? t("graduation_chat_desc")
						: t("graduation_stepper_desc")}
				</p>
			</div>

			{/* Stats */}
			<div className="grid grid-cols-1 gap-4 rounded-xl border bg-card p-5 sm:grid-cols-3">
				<Stat
					icon={Package}
					label={t("stat_products")}
					value={products.data?.page?.totalCount}
					loading={products.isLoading}
					color="bg-blue-500/10 text-blue-600 dark:text-blue-400"
				/>
				<Stat
					icon={ShoppingCart}
					label={t("stat_orders")}
					value={orders.data?.page?.totalCount}
					loading={orders.isLoading}
					color="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
				/>
				<Stat
					icon={Users}
					label={t("stat_contacts")}
					value={parties.data?.page?.totalCount}
					loading={parties.isLoading}
					color="bg-violet-500/10 text-violet-600 dark:text-violet-400"
				/>
			</div>

			{/* What's next */}
			<div className="space-y-2">
				<h2 className="text-sm font-medium text-muted-foreground">
					{t("whats_next")}
				</h2>
				<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
					{NEXT_STEPS.map((ns) => (
						<Button
							key={ns.to}
							variant="outline"
							className="group flex items-start gap-3 rounded-xl p-3 h-auto justify-start hover:border-primary/30 hover:shadow-sm bg-card"
							onClick={() => {
								dismiss();
								navigate({ to: ns.to });
							}}
						>
							<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary">
								<ns.icon className="h-4 w-4" />
							</div>
							<div className="min-w-0">
								<p className="text-sm font-medium truncate">{t(ns.titleKey)}</p>
								<p className="text-xs text-muted-foreground truncate">
									{t(ns.descKey)}
								</p>
							</div>
						</Button>
					))}
				</div>
			</div>

			{/* CTA */}
			<div className="flex justify-center pt-2">
				<Button
					onClick={() => {
						dismiss();
						navigate({ to: "/catalog/products", search: { owner_party_id: undefined } });
					}}
					className="gap-1.5"
				>
					{t("go_to_catalog")}
					<ArrowRight className="h-3.5 w-3.5" />
				</Button>
			</div>
		</div>
	);
}
